import { analyticsService } from '../../../services/analyticsService';
import type { FileAttachment } from '../../../components/common/types/FileAttachment';

export type ChatInputOrigin =
  | 'freeform_text'
  | 'starter_prompt'
  | 'suggested_action'
  | 'follow_up_tag'
  | 'widget_button'
  | 'widget_select'
  | 'widget_multiselect'
  | 'widget_confirm'
  | 'file_only';

interface TrackChatInputArgs {
  origin: ChatInputOrigin;
  /** Which product area the chat turn belongs to. */
  feature: string;
  sessionId?: string;
  informationMode?: boolean;
  attachments?: FileAttachment[];
  /** Widget-originated inputs only. */
  widgetId?: string;
  optionCount?: number;
}

const countWords = (text: string): number => (text.trim() ? text.trim().split(/\s+/).length : 0);

const countSentences = (text: string): number => text.split(/[.!?]+\s|[.!?]+$/).filter((s) => s.trim()).length;

/**
 * Records the *shape* of a chat input, never its content — message text is customer
 * data and must not be persisted in the analytics store.
 */
export const trackChatInput = (text: string, args: TrackChatInputArgs): void => {
  const { origin, feature, sessionId, informationMode, attachments = [], widgetId, optionCount } = args;

  void analyticsService.trackEvent('chat_input_submitted', {
    origin,
    feature,
    session_id: sessionId,
    information_mode: informationMode ?? false,
    char_length: text.length,
    word_count: countWords(text),
    sentence_count: countSentences(text),
    is_question: text.trimEnd().endsWith('?'),
    has_attachments: attachments.length > 0,
    attachment_count: attachments.length,
    attachment_types: Array.from(new Set(attachments.map((a) => a.type).filter(Boolean))),
    ...(widgetId ? { widget_id: widgetId } : {}),
    ...(optionCount !== undefined ? { option_count: optionCount } : {}),
  });
};
